'use client'

import * as React from 'react'
import { useState } from 'react'
import { ZoomIn } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import cn from '@config/tailwind/cn'

import type { MediaType } from './types'

interface GalleryImage {
  image?: MediaType | string
  id?: string
}

interface InteractiveGalleryProps {
  images: GalleryImage[]
  description?: string
}

export function InteractiveGallery({ images, description }: InteractiveGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)

  const validImages = images.filter((item) => {
    const media = typeof item.image === 'string' ? null : item.image
    return media?.url
  })

  if (validImages.length === 0) return null

  const activeMedia = typeof validImages[activeIndex]?.image === 'string' ? null : validImages[activeIndex]?.image

  return (
    <div className='ui:my-8'>
      <div className='ui:flex ui:h-100 ui:gap-2 ui:md:h-125'>
        {validImages.map((item, index) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const media = typeof item.image === 'string' ? null : item.image!
          if (!media?.url) return null
          const isActive = index === activeIndex

          return (
            <motion.div
              key={item.id ?? index}
              className={cn(
                'ui:relative ui:cursor-pointer ui:overflow-hidden ui:rounded-3xl',
                isActive ? 'ui:z-10' : 'ui:z-0',
              )}
              initial={false}
              animate={{
                flex: isActive ? 4 : 1,
              }}
              transition={{
                duration: 0.5,
                ease: [0.32, 0.72, 0, 1],
              }}
              onClick={() => setActiveIndex(index)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <motion.img
                src={media.url}
                alt={media.alt ?? ''}
                className='ui:h-full ui:w-full ui:object-cover'
                loading={isActive ? 'eager' : 'lazy'}
                initial={false}
                animate={{
                  scale: isActive ? 1 : 1.1,
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.32, 0.72, 0, 1],
                }}
              />

              {/* Zoom button */}
              <AnimatePresence>
                {isActive && (
                  <motion.button
                    className='ui:absolute ui:right-4 ui:top-4 ui:bg-neutral-white/20 ui:backdrop-blur-sm ui:text-neutral-white ui:rounded-full ui:p-2 ui:hover:bg-neutral-white/30 ui:transition-colors'
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsZoomed(true)
                    }}
                  >
                    <ZoomIn className='ui:h-5 ui:w-5' />
                  </motion.button>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </div>

      {description && (
        <figcaption className='ui:mt-2 ui:text-center ui:text-sm ui:text-neutral-black'>{description}</figcaption>
      )}

      {/* Zoom modal */}
      <AnimatePresence>
        {isZoomed && activeMedia?.url && (
          <motion.div
            className='ui:fixed ui:inset-0 ui:z-50 ui:flex ui:items-center ui:justify-center ui:bg-neutral-black/90 ui:backdrop-blur-sm ui:cursor-zoom-out'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsZoomed(false)}
          >
            <motion.img
              src={activeMedia.url}
              alt={activeMedia.alt ?? ''}
              className='ui:max-h-[90vh] ui:max-w-[90vw] ui:object-contain ui:rounded-lg'
              loading='eager'
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
